// Guest athletes on a club's roster (see `features/athlete-clubs.md`): athletes
// whose profile belongs to *another* club but who train here too, and can
// therefore be enrolled in this club's training classes.
//
// The distinction this section exists to make visible: a guest is on the roster
// but not owned. This club cannot edit their name, date of birth or photos — that
// stays with their home club — so the section offers exactly two actions, add and
// remove, and no edit. Both need MANAGE_ATHLETES, the same permission as managing
// the club's own athletes.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SportsIcon from '@mui/icons-material/Sports';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import type { Athlete } from '../domain';
import {
  athleteAge,
  isValidPersonalNumber,
  normalizePersonalNumber,
} from '../domain';
import { fetchClubRoster, joinClubRoster, leaveClubRoster } from '../data/apiClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';

interface ClubGuestsSectionProps {
  companyId: number;
}

export function ClubGuestsSection({ companyId }: ClubGuestsSectionProps) {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const canManage = hasPermission('MANAGE_ATHLETES');

  const [roster, setRoster] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [numberError, setNumberError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Athlete | null>(null);

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRoster(await fetchClubRoster(companyId));
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : tRef.current('admin.guests.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  // The roster minus the athletes this club owns — those are the Athletes and
  // Coaches tabs above.
  const guests = useMemo(
    () => roster.filter((athlete) => athlete.companyId !== companyId),
    [roster, companyId],
  );

  const submit = async () => {
    const normalized = normalizePersonalNumber(number);
    if (!isValidPersonalNumber(normalized)) {
      setNumberError(t('admin.guests.add.numberInvalid'));
      return;
    }
    setBusy(true);
    setError(null);
    setNumberError(null);
    try {
      setRoster(await joinClubRoster(companyId, normalized));
      setAddOpen(false);
      setNumber('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.guests.add.failed'));
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setBusy(true);
    setError(null);
    try {
      setRoster(await leaveClubRoster(companyId, removing.personalNumber));
      setRemoving(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.guests.remove.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('admin.guests.title', { count: guests.length })}
        </Typography>
        {canManage && (
          <Button
            size="small"
            startIcon={<PersonAddAlt1Icon />}
            disabled={busy}
            onClick={() => {
              setNumberError(null);
              setAddOpen(true);
            }}
          >
            {t('admin.guests.addAction')}
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.guests.hint')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : guests.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('admin.guests.empty')}
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {guests.map((guest) => {
            const age = athleteAge(guest.dateOfBirth);
            return (
              <Stack
                key={guest.personalNumber}
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ py: 1.5 }}
              >
                <Avatar src={guest.photoUrls[0] ?? undefined} alt={guest.fullName}>
                  {guest.isCoach ? <SportsIcon /> : <DirectionsRunIcon />}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap title={guest.fullName}>
                    {guest.fullName}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={guest.personalNumber}
                      sx={{ fontFamily: 'monospace' }}
                    />
                    {guest.isCoach && (
                      <Chip size="small" color="secondary" label={t('admin.athletes.coachChip')} />
                    )}
                    {age !== null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('admin.athletes.age', { count: age })}
                      />
                    )}
                    <Chip size="small" variant="outlined" label={t('admin.guests.guestChip')} />
                  </Stack>
                </Box>
                {canManage && (
                  <Tooltip title={t('admin.guests.remove.action')}>
                    <span>
                      <IconButton
                        aria-label={t('admin.guests.remove.aria', { name: guest.fullName })}
                        disabled={busy}
                        onClick={() => setRemoving(guest)}
                      >
                        <PersonRemoveIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}

      {/* Add by personal number: the only identifier that reaches across clubs. */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('admin.guests.add.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.guests.add.body')}
          </DialogContentText>
          <TextField
            fullWidth
            autoFocus
            label={t('admin.guests.add.number')}
            value={number}
            onChange={(e) => {
              setNumber(e.target.value);
              setNumberError(null);
            }}
            error={numberError !== null}
            helperText={numberError ?? t('admin.guests.add.numberHelp')}
            slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={busy}>
            {t('admin.common.cancel')}
          </Button>
          <Button variant="contained" onClick={() => void submit()} disabled={busy}>
            {t('admin.guests.add.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={removing !== null} onClose={() => setRemoving(null)}>
        <DialogTitle>{t('admin.guests.remove.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admin.guests.remove.body', { name: removing?.fullName ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)} disabled={busy}>
            {t('admin.common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmRemove()}
            disabled={busy}
          >
            {t('admin.guests.remove.action')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
